import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Clock, DollarSign, Users, ArrowRight, Heart } from 'lucide-react';
import MetaTags from '../components/SEO/MetaTags';
import { pageConfigs } from '../utils/seo';

const Careers: React.FC = () => {
  const jobOpenings = [
    {
      id: 1,
      title: "PT / PTA – Clinic Outreach & Sales Rep",
      department: "SALES",
      location: "Remote (Anywhere in the US)",
      type: "Contract, Flexible",
      salary: "$1,000/month base + $100–$150/clinic commission",
      equity: "Quarterly payouts + residual commissions",
      isRemote: true,
      isClosed: true
    },
    {
      id: 2,
      title: "Computer Vision Engineer / ML Specialist",
      department: "ENGINEERING",
      location: "Remote",
      type: "Part-time (4–6 month contract)",
      salary: "$4K–$6K total",
      equity: "",
      isRemote: true,
      isClosed: true
    }
  ];

  const departments = ["ALL", "SALES", "ENGINEERING"];
  const [selectedDepartment, setSelectedDepartment] = React.useState("ALL");

  const filteredJobs = selectedDepartment === "ALL" 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  return (
    <>
      <MetaTags {...pageConfigs.careers} />

      <div className="min-h-screen bg-gradient-to-br from-[#0a1a1a] via-[#1a2f2f] to-[#2d4a4a]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-6"
            >
              <span className="text-[#58ACDD] text-sm font-medium uppercase tracking-wide">
                Open roles
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold text-white mb-6 font-productSans"
            >
              Where innovation meets impact
              <br />
              <span className="text-[#58ACDD]">Join us in transforming health.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl text-white/80 max-w-2xl mx-auto font-productSans"
            >
              Help us transform healthcare by building AI-powered tools that give clinicians 
              more time to focus on what matters most: patient care.
            </motion.p>
          </div>
        </section>

        {/* Department Filter */}
        <section className="px-4 sm:px-6 lg:px-8 mb-12">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedDepartment === dept
                      ? 'bg-[#58ACDD] text-white shadow-lg'
                      : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Job Listings */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {filteredJobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    {/* Job Info */}
                    <div className="flex-1">
                      <div className="mb-4">
                        <span className="text-[#58ACDD] text-sm font-medium uppercase tracking-wide">
                          {job.department}
                        </span>
                      </div>
                      
                      <h3 className="text-2xl font-bold text-white mb-4 font-productSans">
                        {job.title}
                      </h3>

                      <div className="flex flex-wrap gap-6 text-white/60">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">{job.type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          <span className="text-sm">{job.salary}</span>
                        </div>
                        {job.equity && (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span className="text-sm">{job.equity}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Apply Button */}
                    <div className="lg:flex-shrink-0">
                      {job.isClosed ? (
                        <div className="w-full lg:w-auto bg-gray-500 text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 opacity-60 cursor-not-allowed">
                          CLOSED
                        </div>
                      ) : (
                        <Link
                          to={job.id === 1 ? "/careers/pt-pta-sales-rep" : "/careers/computer-vision-engineer"}
                          className="w-full lg:w-auto bg-gradient-to-r from-[#58ACDD] to-[#486633] text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105 group-hover:shadow-[#58ACDD]/25 flex items-center justify-center gap-2"
                        >
                          Learn More
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {filteredJobs.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16"
              >
                <p className="text-white/60 text-lg">
                  No openings in this department right now. Check back soon!
                </p>
              </motion.div>
            )}
          </div>
        </section>

        {/* Company Culture Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
            >
              <h2 className="text-3xl font-bold text-white mb-6 font-productSans">
                Why Join Fownd?
              </h2>
              <p className="text-white/80 text-lg mb-8 font-productSans">
                We're a team of passionate builders, clinicians, and innovators working to solve 
                one of healthcare's biggest challenges. Join us in creating tools that make a 
                real difference in the lives of healthcare providers and their patients.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#58ACDD]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-[#58ACDD]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Mission-Driven</h3>
                  <p className="text-white/60 text-sm">
                    Every line of code impacts real healthcare providers and patients
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#486633]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ArrowRight className="w-8 h-8 text-[#486633]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Fast Growth</h3>
                  <p className="text-white/60 text-sm">
                    Join us at an exciting stage with significant equity upside
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#58ACDD]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-[#58ACDD]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">Real Impact</h3>
                  <p className="text-white/60 text-sm">
                    See your models deployed in real clinical settings improving patient care
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 lg:px-8 pb-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-r from-[#58ACDD]/10 to-[#486633]/10 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
            >
              <h2 className="text-3xl font-bold text-white mb-4 font-productSans">
                Don't see the right role?
              </h2>
              <p className="text-white/80 text-lg mb-8 font-productSans">
                We're always looking for exceptional talent. Send us your resume and 
                let us know how you'd like to contribute to our mission.
              </p>
              
              <a
                href="mailto:careers@fownd.care"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#58ACDD] to-[#486633] text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                Get in touch
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Careers;
